import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { CarouselProvider, Slider } from 'pure-react-carousel';
import GalleryCarouselPagination from '../GalleryCarouselPagination/GalleryCarouselPagination';
import CarouselButtonPrev from './CarouselButtonPrev';
import CarouselButtonNext from './CarouselButtonNext';
import css from './ListingCardsCarousel.css';

const Carousel = ({
    isMobile,
    items,
    touchEnabled = false,
    sliderVisibleSlides: sliderVisibleSlidesProp,
    sliderClassName,
    carouselHolderClassName,
    hideButtonsOnEndStart = false,
    showNextButton: showNextButtonProp = true,
    showPrevButton: showPrevButtonProp = true,
    showDots: showDotsProp = true,
    minItemsCount = 1,
    rendererSingleItem,
    rendererCarousel,
    renderPrevButton,
    renderNextButton,
    isIntrinsicHeight = true,
    naturalSlideWidth = 100,
    naturalSlideHeight = 100,
    notifyOnSlideChange = () => null,
    currentSlide = 0,
    ...rest
}) => {
    const [sliderCurrentIndex, setSliderCurrentIndex] = useState(currentSlide);

    const slider = useRef();

    const handlerSliderTransition = () => {
        if (slider && slider.current) {
            const currentSlide = Math.round(slider?.current?.state?.currentSlide || 0);
            setSliderCurrentIndex(currentSlide);
            notifyOnSlideChange(currentSlide);
        }
    };

    const showAsSingleItem =
        items && ((items.length < minItemsCount && !isMobile) || items.length === 1);

    const showAsSlider =
        !showAsSingleItem &&
        items &&
        ((!isMobile && items.length >= minItemsCount) || (isMobile && items.length > 1));

    const { length: itemsNum } = items || [];

    const sliderVisibleSlides =
        sliderVisibleSlidesProp || (isMobile && showAsSingleItem ? 1 : isMobile ? 1.1 : 2.2);

    if (itemsNum === 0) {
        return null;
    }
    /**
     * before change, check for 3 elems per slider
     */
    const totalSliderSteps = Math.max(Math.ceil(itemsNum - sliderVisibleSlides) + 1, itemsNum);
    const total = sliderVisibleSlides < 2 ? itemsNum : totalSliderSteps;

    const minItemsToShowDots = isMobile ? 1 : 2;
    const showDots =
        showDotsProp && (total > minItemsToShowDots || total === Math.ceil(sliderVisibleSlides));

    const showNextButton = showNextButtonProp && total > minItemsToShowDots;
    const showPrevButton = showPrevButtonProp && total > minItemsToShowDots;

    return (
        <div
            className={classNames(css.sliderOuterContainer, {
                [css.rightOverlayHidden]:
                    Math.ceil(sliderCurrentIndex) + Math.round(sliderVisibleSlides) >= itemsNum,
                [sliderClassName]: !!sliderClassName,
            })}
        >
            {showAsSingleItem && (
                <div className={css.singleItemsWrapper}>{items.map(rendererSingleItem)}</div>
            )}
            {showAsSlider && (
                <div
                    className={classNames(css.carouselHolder, {
                        [carouselHolderClassName]: !!carouselHolderClassName,
                    })}
                >
                    <CarouselProvider
                        naturalSlideWidth={naturalSlideWidth}
                        naturalSlideHeight={naturalSlideHeight}
                        isIntrinsicHeight={isIntrinsicHeight}
                        totalSlides={itemsNum}
                        visibleSlides={sliderVisibleSlides}
                        infinite={false}
                        dragEnabled={touchEnabled}
                        touchEnabled={touchEnabled}
                        currentSlide={currentSlide}
                        {...rest}
                    >
                        <Slider
                            className={classNames(css.sliderWrapper, {
                                [css.singleItemWrapper]: sliderVisibleSlides === 1,
                            })}
                            ref={slider}
                            onTransitionEnd={handlerSliderTransition}
                            classNameAnimation={css.sliderAnimation}
                        >
                            {items.map(rendererCarousel)}
                        </Slider>
                        <CarouselButtonPrev
                            show={showPrevButton}
                            renderContent={renderPrevButton}
                            hideOnStartEnd={hideButtonsOnEndStart}
                        />
                        <CarouselButtonNext
                            show={showNextButton}
                            renderContent={renderNextButton}
                            hideOnStartEnd={hideButtonsOnEndStart}
                        />
                        {showDots && (
                            <GalleryCarouselPagination
                                index={sliderCurrentIndex}
                                slideCount={itemsNum}
                                rootClassName={css.paginationWrapper}
                            />
                        )}
                    </CarouselProvider>
                </div>
            )}
        </div>
    );
};

export default Carousel;
